import { ActionTree, ActionContext } from 'vuex';

import { calendarClient } from '@/http-client';
import { camelToSnake, snakeToCamel } from '@/utils';
import { getCalendarColor, saveCalendarColor } from '@/utils/calendar-colors';

import { RootStateI } from '../state';
import { CalendarStateI, CalendarI } from './state';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isSameCalendarId(
  left: number | string,
  right: number | string,
) {
  return String(left) === String(right);
}

function mergeCalendars(
  localCalendars: CalendarI[],
  remoteCalendars: CalendarI[],
): CalendarI[] {
  const merged = [...remoteCalendars];

  localCalendars.forEach((localCalendar) => {
    const exists = merged.some((remoteCalendar) => (
      isSameCalendarId(remoteCalendar.id, localCalendar.id)
    ));

    if (!exists) {
      merged.push(localCalendar);
    }
  });

  return merged;
}

function isCalendar(calendar: CalendarI | null): calendar is CalendarI {
  return calendar !== null;
}

function normalizeCalendar(
  data: unknown,
  fallback: Partial<CalendarI> = {},
): CalendarI | null {
  const normalized = snakeToCamel(data);
  let calendar = normalized;

  if (isRecord(normalized)) {
    calendar = normalized.calendar ?? normalized.data ?? normalized;
  }

  if (!isRecord(calendar)) {
    return null;
  }

  const id = (calendar.id ?? calendar.calendarId ?? calendar.insertId ?? fallback.id) as string | number | undefined | null;
  if (id === undefined || id === null) {
    return null;
  }

  const color = (calendar.color as string | undefined) || getCalendarColor(id) || fallback.color;
  if (id && color) {
    saveCalendarColor(id, color);
  }

  return {
    ...fallback,
    ...calendar,
    id,
    color,
  } as CalendarI;
}

function normalizeCalendarList(data: unknown): CalendarI[] {
  const normalized = snakeToCamel(data);
  console.log('[normalizeCalendarList] data:', data);
  console.log('[normalizeCalendarList] normalized:', normalized);

  if (Array.isArray(normalized)) {
    const result = normalized.map((calendar) => normalizeCalendar(calendar)).filter(isCalendar);
    console.log('[normalizeCalendarList] result from array:', result);
    return result;
  }

  if (isRecord(normalized)) {
    if (Array.isArray(normalized.calendars)) {
      const result = normalized.calendars.map((calendar) => normalizeCalendar(calendar)).filter(isCalendar);
      console.log('[normalizeCalendarList] result from calendars:', result);
      return result;
    }

    if (Array.isArray(normalized.data)) {
      const result = normalized.data.map((calendar) => normalizeCalendar(calendar)).filter(isCalendar);
      console.log('[normalizeCalendarList] result from data:', result);
      return result;
    }

    // Check if the object itself looks like a calendar
    if (normalized.id || normalized.calendarId) {
      const calendar = normalizeCalendar(normalized);
      console.log('[normalizeCalendarList] result from single calendar:', calendar);
      return calendar ? [calendar] : [];
    }
  }

  console.log('[normalizeCalendarList] returning empty array');
  return [];
}

export const actions: ActionTree<CalendarStateI, RootStateI> = {
  async create(
    context: ActionContext<CalendarStateI, RootStateI>,
    payload: Partial<CalendarI>,
  ): Promise<CalendarI> {
    const { data } = await calendarClient.post('/api/calendars/create', camelToSnake(payload));
    console.log('[calendars/create] raw response data:', data);
    const calendar = normalizeCalendar(data, payload);
    console.log('[calendars/create] normalized calendar:', calendar);

    if (!calendar) {
      console.warn('[calendars/create] normalization failed, not committing addCalendar');
      return data as CalendarI;
    }

    if (calendar.id && calendar.color) {
      saveCalendarColor(calendar.id, calendar.color);
    }

    context.commit('addCalendar', calendar);
    return calendar;
  },

  async list(
    context: ActionContext<CalendarStateI, RootStateI>,
  ): Promise<CalendarI[]> {
    const { data } = await calendarClient.get('/api/calendars/list');
    console.log('[calendars/list] raw data from API:', data);
    const calendars = normalizeCalendarList(data);
    console.log('[calendars/list] parsed calendars:', calendars);
    console.log('[calendars/list] current state calendars:', context.state.calendars);

    if (calendars.length === 0) {
      console.log('[calendars/list] no calendars from API, checking local state');
      if (context.state.calendars.length > 0) {
        console.log('[calendars/list] returning local calendars:', context.state.calendars);
        return context.state.calendars;
      }

      console.log('[calendars/list] creating General calendar');
      const calendar = await context.dispatch('create', {
        name: 'General',
        app: 'calendar',
      }) as CalendarI;

      context.commit('setCalendars', [calendar]);
      return [calendar];
    }

    const mergedCalendars = mergeCalendars(context.state.calendars, calendars);
    console.log('[calendars/list] merged calendars:', mergedCalendars);
    context.commit('setCalendars', mergedCalendars);
    return mergedCalendars;
  },

  async edit(
    context: ActionContext<CalendarStateI, RootStateI>,
    payload: Partial<CalendarI>,
  ): Promise<CalendarI> {
    const { data } = await calendarClient.post('/api/calendars/edit', camelToSnake(payload));
    const calendar = normalizeCalendar(data, payload);

    if (calendar) {
      if (calendar.id && calendar.color) {
        saveCalendarColor(calendar.id, calendar.color);
      }
      context.commit('addCalendar', calendar);
    }
    return calendar || (data as CalendarI);
  },

  async delete(
    context: ActionContext<CalendarStateI, RootStateI>,
    calendarId: number | string,
  ): Promise<void> {
    await calendarClient.get(`/api/calendars/delete/${calendarId}`);
    context.commit('removeCalendar', calendarId);
  },

};
